import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "./constant_api";
import { useSearchParams } from "react-router-dom";



const SelectBankAccountModal = ({
  isOpen,
  organisationId,
  currentUserId,
  onClose,
  onSubmit,
}) => {
  // ---- step control: "accounts" -> "users" ----
  const [step, setStep] = useState("accounts");

  // ---- accounts state ----
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState("");

  // ---- users state ----
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [searchParams] = useSearchParams();
  const OrganisationID = searchParams.get("OrganisationID") || organisationId || "";

  useEffect(() => {
    if (isOpen && OrganisationID) {
      // reset state each time modal opens
      setStep("accounts");
      setSelectedAccountIds([]);
      setSelectedUserIds([]);
      setError("");
      setAccountsError("");
      setUsersError("");
      fetchBankAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, OrganisationID]);

  const fetchBankAccounts = async () => {
    setAccountsLoading(true);
    setAccountsError("");
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation/get-bank-accounts`,
        {
          params: { OrganisationID: OrganisationID },
        }
      );

      if (response?.data?.success) {
        setAccounts(response.data.data || []);
      } else {
        setAccountsError(response?.data?.message || "Failed to fetch bank accounts");
      }
    } catch (err) {
      console.error("Error fetching bank accounts:", err);
      setAccountsError(
        err?.response?.data?.message || "Something went wrong while fetching accounts"
      );
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchXeroUsers = async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation/get-xero-users`,
        {
          params: { OrganisationID: OrganisationID },
        }
      );

      if (response?.data?.success) {
        const fetchedUsers = response.data.users || [];
        // Exclude the currently logged-in user from the removable list
        // so they can't accidentally remove their own access.
        const filteredUsers = currentUserId
          ? fetchedUsers.filter((u) => u.UserID !== currentUserId)
          : fetchedUsers;
        setUsers(filteredUsers);
      } else {
        setUsersError(response?.data?.message || "Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching Xero users:", err);
      setUsersError(
        err?.response?.data?.message || "Something went wrong while fetching users"
      );
    } finally {
      setUsersLoading(false);
    }
  };

  // ---- accounts handlers ----
  const handleAccountCheckboxChange = (accountId) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAllAccounts = () => {
    if (selectedAccountIds.length === accounts.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(accounts.map((acc) => acc.AccountID));
    }
  };

  // ---- users handlers ----
  const handleUserCheckboxChange = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map((u) => u.UserID));
    }
  };

  // ---- step navigation ----
  // After accounts step: if there are users to pick from, go to the users step.
  // If there are none (length 0), skip straight to submit.
  const handleAccountsNext = async () => {
    if (selectedAccountIds.length === 0) {
      setError("Please select at least one account");
      return;
    }
    setError("");
    await fetchXeroUsers();
    setStep("users");
  };

  const handleBackToAccounts = () => {
    setStep("accounts");
    setError("");
  };

 const handleSubmit = async () => {
  setSubmitting(true);
  setError("");

  try {
    // =====================================================
    // STEP 1: FULL SYNC
    // =====================================================

    const syncResponse = await axios.post(
      `${BASE_URL}/organisation/sync-new-data`,
      {
        OrganisationID,
        Accounts: selectedAccountIds,
        Users: [...selectedUserIds, currentUserId],
        CurrentUserID: currentUserId,
      }
    );

    // -----------------------------------------------------
    // VERY IMPORTANT
    // -----------------------------------------------------
    // Do NOT continue if full sync failed.
    // -----------------------------------------------------

    if (syncResponse?.data?.success !== true) {
      setError(
        syncResponse?.data?.message ||
          "Full sync failed. Data was not deleted."
      );

      return;
    }

    // =====================================================
    // STEP 2: DELETE UNSELECTED DATA
    // =====================================================

    const removeResponse = await axios.post(
      `${BASE_URL}/organisation/remove-selected-data`,
      {
        OrganisationID,
        Accounts: selectedAccountIds,
        Users: [...selectedUserIds, currentUserId],
        CurrentUserID: currentUserId,
      }
    );

    // -----------------------------------------------------
    // CHECK DELETE RESPONSE
    // -----------------------------------------------------

    if (removeResponse?.data?.success !== true) {
      setError(
        removeResponse?.data?.message ||
          "Failed to remove unselected data."
      );

      return;
    }
    
    // =====================================================
    // SUCCESS
    // =====================================================

    onSubmit?.(
      selectedAccountIds,
      selectedUserIds,
      removeResponse?.data
    );

    handleClose();

    window.location.href = "/query";
  } catch (err) {
    console.error(
      "Error submitting selected accounts/users:",
      err
    );

    setError(
      err?.response?.data?.message ||
        "Failed to submit selected accounts/users"
    );
  } finally {
    setSubmitting(false);
  }
};

  // Auto-effect: once users have loaded on the users step, if there are
  // no users at all, skip straight through to submit.
  // useEffect(() => {
  //   if (step === "users" && !usersLoading && users.length === 0) {
  //     handleSubmit();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [step, usersLoading, users]);

  const handleClose = () => {
    setStep("accounts");
    setSelectedAccountIds([]);
    setSelectedUserIds([]);
    setError("");
    setAccountsError("");
    setUsersError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sba-modal-overlay">
      <div className="sba-modal">
        <div className="sba-modal-header">
          <h4>
            {step === "accounts" ? "Select Bank Accounts" : "Select Users"}
          </h4>
          <button
            type="button"
            className="sba-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="sba-modal-body">
          {step === "accounts" && (
            <>
              {accountsLoading && (
                <div className="sba-loading">
                  <div className="sk-spinner sk-spinner-pulse"></div>
                  <span>Loading accounts...</span>
                </div>
              )}

              {!accountsLoading && accountsError && (
                <div className="sba-error">{accountsError}</div>
              )}

              {!accountsLoading && !accountsError && accounts.length === 0 && (
                <div className="sba-empty">No bank accounts found.</div>
              )}

              {!accountsLoading && accounts.length > 0 && (
                <>
                  <div className="sba-select-all-row">
                    <label className="sba-checkbox-label">
                      <input
                        type="checkbox"
                        checked={
                          selectedAccountIds.length === accounts.length &&
                          accounts.length > 0
                        }
                        onChange={handleSelectAllAccounts}
                      />
                      <span>Select All</span>
                    </label>
                  </div>

                  <div className="sba-account-list">
                    {accounts.map((account) => (
                      <div className="sba-account-row" key={account.AccountID}>
                        <label className="sba-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedAccountIds.includes(account.AccountID)}
                            onChange={() => handleAccountCheckboxChange(account.AccountID)}
                          />
                          <div className="sba-account-info">
                            <span className="sba-account-name">
                              {account.Name}
                            </span>
                            <span className="sba-account-meta">
                              {account.BankAccountType} &middot; {account.CurrencyCode}
                              {account.BankAccountNumber
                                ? ` \u00b7 ****${account.BankAccountNumber.slice(-4)}`
                                : ""}
                            </span>
                          </div>
                          <span
                            className={`sba-status-badge sba-status-${(
                              account.Status || ""
                            ).toLowerCase()}`}
                          >
                            {account.Status}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {step === "users" && (
            <>
              {usersLoading && (
                <div className="sba-loading">
                  <div className="sk-spinner sk-spinner-pulse"></div>
                  <span>Loading users...</span>
                </div>
              )}

              {!usersLoading && usersError && (
                <div className="sba-error">{usersError}</div>
              )}

              {!usersLoading && !usersError && users.length === 0 && (
                <div className="sba-empty">
                  No users found. Submitting your account selection...
                </div>
              )}

              {!usersLoading && users.length > 0 && (
                <>
                  <div className="sba-select-all-row">
                    <label className="sba-checkbox-label">
                      <input
                        type="checkbox"
                        checked={
                          selectedUserIds.length === users.length &&
                          users.length > 0
                        }
                        onChange={handleSelectAllUsers}
                      />
                      <span>Select All</span>
                    </label>
                  </div>

                  <div className="sba-account-list">
                    {users.map((user) => (
                      <div className="sba-account-row" key={user.UserID}>
                        <label className="sba-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.UserID)}
                            onChange={() => handleUserCheckboxChange(user.UserID)}
                          />
                          <div className="sba-account-info">
                            <span className="sba-account-name">
                              {user.FirstName} {user.LastName}
                            </span>
                            <span className="sba-account-meta">
                              {user.EmailAddress}
                              {user.OrganisationRole ? ` \u00b7 ${user.OrganisationRole}` : ""}
                            </span>
                          </div>
                          {user.IsSubscriber && (
                            <span className="sba-status-badge sba-status-active">
                              Subscriber
                            </span>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {error && <div className="sba-error">{error}</div>}
        </div>

        <div className="sba-modal-footer">
          <span className="sba-selected-count">
            {step === "accounts"
              ? `${selectedAccountIds.length} account(s) selected`
              : `${selectedUserIds.length} user(s) selected`}
          </span>
          <div className="sba-footer-actions">
            {step === "accounts" && (
              <>
                <button
                  type="button"
                  className="sba-btn sba-btn-secondary"
                  onClick={handleClose}
                  disabled={accountsLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sba-btn sba-btn-primary"
                  onClick={handleAccountsNext}
                  disabled={
                    accountsLoading || usersLoading || selectedAccountIds.length === 0
                  }
                >
                  {usersLoading ? "Loading..." : "Next"}
                </button>
              </>
            )}

            {step === "users" && (
              <>
                <button
                  type="button"
                  className="sba-btn sba-btn-secondary"
                  onClick={handleBackToAccounts}
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="sba-btn sba-btn-secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sba-btn sba-btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting || usersLoading}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectBankAccountModal;